import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faPlus,
  faPenToSquare,
  faMagnifyingGlass,
  faChevronLeft,
  faChevronRight,
  faMinus,
  faCopy,
  faRightLeft,
  faRotateLeft,
  faBan,
} from "@fortawesome/free-solid-svg-icons";
import { productService } from "../services/product";
import { stockService } from "../services/stock";
import { productionService } from "../services/production";
import toast from "react-hot-toast";

const Stock = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    searchTerm: "",
  });
  const [localSearchTerm, setLocalSearchTerm] = useState(""); // Novo estado para o input de busca
  const itemsPerPage = 12; // 12 cards por página para grid responsivo
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10));

  // Estados para o modal de quebra manual
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [availableLots, setAvailableLots] = useState([]);
  const [selectedLot, setSelectedLot] = useState("");
  const [withdrawalQuantity, setWithdrawalQuantity] = useState("");
  const [withdrawalReason, setWithdrawalReason] = useState("");
  const [withdrawalObservations, setWithdrawalObservations] = useState("");
  const [isSubmittingWithdrawal, setIsSubmittingWithdrawal] = useState(false);
  const [loadingLots, setLoadingLots] = useState(false);
  const [withdrawalType, setWithdrawalType] = useState('quebra'); // 'quebra' | 'cura'

  // Motivos predefinidos para quebra de estoque
  const breakageReasons = [
    "Produto Danificado",
    "Produto Vencido",
    "Perda de Qualidade",
    "Contaminação",
    "Erro de Produção",
    "Problema na Cura",
    "Outros"
  ];

  // Estados para o modal de criação de variante
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [selectedProductForVariant, setSelectedProductForVariant] = useState(null);
  const [variantData, setVariantData] = useState({
    product_code: "",
    name: "",
    initial_weight_kg: "",
  });
  const [isSubmittingVariant, setIsSubmittingVariant] = useState(false);

  // Estados para o modal de reversão (produção ou rejeição)
  const [showUndoModal, setShowUndoModal] = useState(false);
  const [undoProductId, setUndoProductId] = useState(null);
  const [undoProductName, setUndoProductName] = useState(null);
  const [undoCategory, setUndoCategory] = useState(null); // 'production' ou 'rejection'
  const [availableItems, setAvailableItems] = useState([]); // Produções ou rejeições
  const [selectedItem, setSelectedItem] = useState(null);
  const [loadingItems, setLoadingItems] = useState(false);
  const [isSubmittingUndo, setIsSubmittingUndo] = useState(false);

  // Estados para o modal de transferência de lote entre produtos
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferSourceProductId, setTransferSourceProductId] = useState("");
  const [transferTargetProductId, setTransferTargetProductId] = useState("");
  const [transferLotId, setTransferLotId] = useState("");
  const [transferQuantity, setTransferQuantity] = useState("");
  const [transferNewLotName, setTransferNewLotName] = useState("");
  const [transferSourceLots, setTransferSourceLots] = useState([]);
  const [transferCompatibleProducts, setTransferCompatibleProducts] = useState([]);
  const [loadingTransferData, setLoadingTransferData] = useState(false);
  const [isSubmittingTransfer, setIsSubmittingTransfer] = useState(false);

  // Estados para o modal de produtos desativados
  const [showInactiveProductsModal, setShowInactiveProductsModal] = useState(false);
  const [inactiveProducts, setInactiveProducts] = useState([]);
  const [loadingInactiveProducts, setLoadingInactiveProducts] = useState(false);
  const [reactivatingProductId, setReactivatingProductId] = useState(null);
  const [deactivatingProductId, setDeactivatingProductId] = useState(null);

  const navigate = useNavigate();

  // Debounce de 4s antes de disparar a busca ao backend, evitando uma
  // consulta a cada tecla digitada.
  const debouncedSearchTerm = useDebouncedValue(localSearchTerm, 4000);
  useEffect(() => {
    setFilters((prev) => ({ ...prev, searchTerm: debouncedSearchTerm }));
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError("");

        // Verificar cura concluída automaticamente (silenciosamente)
        try {
          await productionService.checkCuringCompletion();
        } catch (curingError) {
          // Não mostrar erro ao usuário, apenas registrar no console
          console.warn("Erro ao verificar cura:", curingError);
          toast.error("Não foi possível atualizar a cura. Os saldos podem estar desatualizados.");
        }

        // O backend já retorna 'current_stock_kg' em cada produto.
        const fetchedProducts = await productService.getProducts(filters);
        setProducts(Array.isArray(fetchedProducts) ? fetchedProducts : []);
      } catch (err) {
        // Em caso de erro, definir products como vazio (não mostrar erro)
        console.error("Erro ao buscar produtos:", err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [filters]);

  // ⭐ LISTENER SIMPLES - Monitor localStorage para refazer fetch quando um produto é rejeitado
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'forceStockRefresh') {
        console.log('%c🔄 Stock: Refazendo fetch de produtos...', 'color: green; font-size: 14px; font-weight: bold');

        // Refazer fetch dos produtos
        const fetchProducts = async () => {
          try {
            setLoading(true);
            const fetchedProducts = await productService.getProducts(filters);
            setProducts(fetchedProducts);
          } catch (err) {
            console.error("Stock: Erro ao atualizar stock:", err);
          } finally {
            setLoading(false);
          }
        };

        fetchProducts();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    console.log('Stock: 👂 Listener ativado - aguardando rejeições...');

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === "searchTerm") {
      setLocalSearchTerm(value); // Atualiza o estado local imediatamente
    } else {
      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleCreateProduct = () => {
    navigate("/create-products");
  };

  const refreshProducts = async () => {
    const fetchedProducts = await productService.getProducts(filters);
    setProducts(Array.isArray(fetchedProducts) ? fetchedProducts : []);
  };

  const handleEditProduct = (id) => {
    navigate(`/edit-product/${id}`);
  };

  const handleViewDetails = (id) => {
    navigate(`/product-details/${id}`);
  };

  const setCurrentPage = (page) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', page.toString());
      return newParams;
    });
  };

  // Reset para a página 1 só quando o termo de busca (já com debounce) muda
  // de verdade — não na montagem inicial, para não perder a página da URL
  // (ex.: ao voltar de "Detalhes do Produto").
  const isFirstSearchRun = useRef(true);
  useEffect(() => {
    if (isFirstSearchRun.current) {
      isFirstSearchRun.current = false;
      return;
    }
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.searchTerm]);

  // Calculate pagination
  const totalItems = products.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = products.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const loadInactiveProducts = async () => {
    try {
      setLoadingInactiveProducts(true);
      const products = await stockService.getInactiveProducts();
      setInactiveProducts(Array.isArray(products) ? products : []);
    } catch (error) {
      console.error("Erro ao buscar produtos desativados:", error);
      toast.error(error.message || "Erro ao buscar produtos desativados");
      setInactiveProducts([]);
    } finally {
      setLoadingInactiveProducts(false);
    }
  };

  const handleOpenInactiveProductsModal = async () => {
    setShowInactiveProductsModal(true);
    await loadInactiveProducts();
  };

  const handleCloseInactiveProductsModal = () => {
    setShowInactiveProductsModal(false);
    setInactiveProducts([]);
    setReactivatingProductId(null);
  };

  const handleReactivateProduct = async (productId) => {
    try {
      setReactivatingProductId(productId);
      await stockService.reactivateProduct(productId);
      toast.success("Produto reativado com sucesso");
      await Promise.all([loadInactiveProducts(), refreshProducts()]);
    } catch (error) {
      console.error("Erro ao reativar produto:", error);
      toast.error(error.message || "Erro ao reativar produto");
    } finally {
      setReactivatingProductId(null);
    }
  };

  const handleDeactivateProduct = async (product) => {
    if (!window.confirm(`Desativar ${product.name}? O produto sairá da tela de stock.`)) {
      return;
    }

    try {
      setDeactivatingProductId(product.id);
      await stockService.deactivateProduct(product.id);
      toast.success("Produto desativado com sucesso");
      await Promise.all([
        refreshProducts(),
        showInactiveProductsModal ? loadInactiveProducts() : Promise.resolve(),
      ]);
    } catch (error) {
      console.error("Erro ao desativar produto:", error);
      toast.error(error.message || "Erro ao desativar produto");
    } finally {
      setDeactivatingProductId(null);
    }
  };

  // useEffect para buscar lotes quando produto for selecionado
  useEffect(() => {
    const fetchLots = async () => {
      if (!selectedProduct) {
        setAvailableLots([]);
        setSelectedLot("");
        return;
      }

      try {
        setLoadingLots(true);
        // Pedir só os lotes do tipo de baixa em curso: um lote em cura não pode
        // ser candidato numa quebra de stock disponível, e vice-versa.
        const lots = await stockService.getLotsByProduct(
          selectedProduct,
          withdrawalType === "cura" ? "curing" : "available"
        );
        setAvailableLots(Array.isArray(lots) ? lots : []);
        setSelectedLot("");
      } catch (error) {
        console.error("Erro ao buscar lotes:", error);
        toast.error("Erro ao buscar lotes do produto");
        setAvailableLots([]);
      } finally {
        setLoadingLots(false);
      }
    };

    fetchLots();
  }, [selectedProduct, withdrawalType]);

  // Lotes elegíveis para o tipo de baixa em curso. O backend já devolve só
  // estes, mas o filtro fica aqui também para que nenhum lote de outro estado
  // possa ser selecionado se a resposta vier de uma versão antiga da API.
  const eligibleLots = useMemo(
    () =>
      availableLots.filter((lot) =>
        withdrawalType === "cura" ? lot.status === "curing" : lot.status === "available"
      ),
    [availableLots, withdrawalType]
  );

  // Auto-selecionar quando existe exatamente um lote elegível, e largar a
  // seleção assim que o lote escolhido deixar de constar da lista elegível.
  useEffect(() => {
    if (eligibleLots.length === 1) {
      setSelectedLot(eligibleLots[0].id);
      return;
    }
    setSelectedLot((atual) =>
      atual && eligibleLots.some((lot) => lot.id === atual) ? atual : ""
    );
  }, [eligibleLots]);

  // useEffect para buscar lotes disponíveis e produtos compatíveis quando o produto de origem da transferência muda
  useEffect(() => {
    const fetchTransferSourceData = async () => {
      if (!transferSourceProductId) {
        setTransferSourceLots([]);
        setTransferCompatibleProducts([]);
        return;
      }

      try {
        setLoadingTransferData(true);
        const [lots, compatibleProducts] = await Promise.all([
          stockService.getLotsByProduct(transferSourceProductId),
          productService.getTransferLinks(transferSourceProductId),
        ]);
        setTransferSourceLots(
          Array.isArray(lots)
            ? lots.filter((lot) => lot.status === "available" && lot.available_quantity > 0)
            : []
        );
        setTransferCompatibleProducts(Array.isArray(compatibleProducts) ? compatibleProducts : []);
      } catch (error) {
        console.error("Erro ao buscar dados para transferência:", error);
        toast.error("Erro ao buscar lotes e produtos compatíveis");
        setTransferSourceLots([]);
        setTransferCompatibleProducts([]);
      } finally {
        setLoadingTransferData(false);
      }
    };

    setTransferLotId("");
    setTransferTargetProductId("");
    setTransferNewLotName("");
    fetchTransferSourceData();
  }, [transferSourceProductId]);

  // Auto-preencher o nome do lote de destino quando produto destino ou lote de origem mudam
  useEffect(() => {
    if (!transferTargetProductId || !transferLotId) {
      setTransferNewLotName("");
      return;
    }
    const targetProduct = transferCompatibleProducts.find((p) => p.id === transferTargetProductId);
    const sourceLot = transferSourceLots.find((l) => l.id === transferLotId);
    if (!targetProduct || !sourceLot) return;
    const code = targetProduct.product_code || "";
    const date = sourceLot.production_date; // YYYY-MM-DD
    let datePart = "";
    if (date) {
      const [y, m, d] = date.split("-");
      datePart = `${d}${m}${y}`;
    }
    setTransferNewLotName(datePart ? `${code}-${datePart}` : code);
  }, [transferTargetProductId, transferLotId, transferCompatibleProducts, transferSourceLots]);

  // Parsear data a partir do número do lote (COD-DDMMYYYY ou COD-YYYYMMDD)
  const parseLotDate = (lotNumber) => {
    if (!lotNumber) return null;
    const parts = lotNumber.split(/[-_]/);
    if (parts.length < 2) return null;
    const datePart = parts[1];
    if (datePart.length !== 8) return null;
    const firstFour = parseInt(datePart.substring(0, 4), 10);
    let day, month, year;
    if (firstFour >= 2000 && firstFour <= 2099) {
      year = firstFour;
      month = parseInt(datePart.substring(4, 6), 10);
      day = parseInt(datePart.substring(6, 8), 10);
    } else {
      day = parseInt(datePart.substring(0, 2), 10);
      month = parseInt(datePart.substring(2, 4), 10);
      year = parseInt(datePart.substring(4, 8), 10);
    }
    if (!day || !month || !year) return null;
    return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
  };

  // Handlers para quebra manual
  const handleOpenWithdrawalModal = () => {
    setShowWithdrawalModal(true);
    setWithdrawalType('quebra');
    setSelectedProduct("");
    setAvailableLots([]);
    setSelectedLot("");
    setWithdrawalQuantity("");
    setWithdrawalReason("");
    setWithdrawalObservations("");
  };

  const handleCloseWithdrawalModal = () => {
    setShowWithdrawalModal(false);
    setWithdrawalType('quebra');
    setSelectedProduct("");
    setAvailableLots([]);
    setSelectedLot("");
    setWithdrawalQuantity("");
    setWithdrawalReason("");
    setWithdrawalObservations("");
  };

  const handleSubmitWithdrawal = async (e) => {
    e.preventDefault();

    if (!selectedProduct || !selectedLot || !withdrawalQuantity || !withdrawalReason) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    const product = products.find(p => p.id === selectedProduct);
    if (!product) {
      toast.error("Produto não encontrado");
      return;
    }

    const lot = eligibleLots.find(l => l.id === selectedLot);
    if (!lot) {
      toast.error("Lote não encontrado");
      return;
    }

    if (parseFloat(withdrawalQuantity) > lot.available_quantity) {
      toast.error(`Quantidade maior que o disponível no lote (${lot.available_quantity} kg)`);
      return;
    }

    try {
      setIsSubmittingWithdrawal(true);

      // Combinar motivo e observações para o campo reason
      const fullReason = withdrawalObservations
        ? `${withdrawalReason} - ${withdrawalObservations}`
        : withdrawalReason;

      await stockService.manualWithdrawal({
        product_id: selectedProduct,
        lot_id: selectedLot,
        quantity_kg: parseFloat(withdrawalQuantity),
        reason: fullReason,
      });

      toast.success("Quebra de estoque registrada com sucesso!");
      handleCloseWithdrawalModal();

      // Recarregar produtos
      const fetchedProducts = await productService.getProducts(filters);
      setProducts(fetchedProducts);
    } catch (err) {
      toast.error(err.message || "Erro ao registrar quebra de estoque");
      console.error("Erro ao registrar quebra de estoque:", err);
    } finally {
      setIsSubmittingWithdrawal(false);
    }
  };

  // Reset lote selecionado quando o tipo de baixa muda
  useEffect(() => {
    setSelectedLot("");
    setWithdrawalQuantity("");
  }, [withdrawalType]);

  const getModalProducts = () => {
    if (withdrawalType === 'cura') {
      return products.filter(p => (p.curing_quantity_kg || p.curing_stock_kg || 0) > 0);
    }
    return products.filter(p => p.current_stock_kg > 0);
  };

  // Handlers para transferência de lote entre produtos
  const handleOpenTransferModal = () => {
    setShowTransferModal(true);
    setTransferSourceProductId("");
    setTransferTargetProductId("");
    setTransferLotId("");
    setTransferQuantity("");
    setTransferNewLotName("");
    setTransferSourceLots([]);
    setTransferCompatibleProducts([]);
  };

  const handleCloseTransferModal = () => {
    setShowTransferModal(false);
    setTransferSourceProductId("");
    setTransferTargetProductId("");
    setTransferLotId("");
    setTransferQuantity("");
    setTransferNewLotName("");
    setTransferSourceLots([]);
    setTransferCompatibleProducts([]);
  };

  const handleSubmitTransfer = async (e) => {
    e.preventDefault();

    if (!transferSourceProductId || !transferTargetProductId || !transferLotId || !transferQuantity) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    const lot = transferSourceLots.find((l) => l.id === transferLotId);
    if (lot && parseFloat(transferQuantity) > lot.available_quantity) {
      toast.error(`Quantidade maior que o disponível no lote (${lot.available_quantity} kg)`);
      return;
    }

    try {
      setIsSubmittingTransfer(true);

      await stockService.transferStock({
        source_product_id: transferSourceProductId,
        target_product_id: transferTargetProductId,
        source_lot_id: transferLotId,
        quantity_kg: parseFloat(transferQuantity),
        new_lot_name: transferNewLotName.trim() || undefined,
      });

      toast.success("Transferência de lote realizada com sucesso!");
      handleCloseTransferModal();

      // Recarregar produtos
      const fetchedProducts = await productService.getProducts(filters);
      setProducts(fetchedProducts);
    } catch (err) {
      toast.error(err.message || "Erro ao transferir lote");
      console.error("Erro ao transferir lote:", err);
    } finally {
      setIsSubmittingTransfer(false);
    }
  };

  // Handlers para criação de variante
  const handleOpenVariantModal = (product) => {
    // Não permitir criar variante de uma variante
    if (product.is_variant) {
      toast.error("Não é possível criar variante de uma variante");
      return;
    }

    setSelectedProductForVariant(product);
    setVariantData({
      product_code: "",
      name: "",
      initial_weight_kg: product.initial_weight_kg || "",
    });
    setShowVariantModal(true);
  };

  const handleCloseVariantModal = () => {
    setShowVariantModal(false);
    setSelectedProductForVariant(null);
    setVariantData({
      product_code: "",
      name: "",
      initial_weight_kg: "",
    });
  };

  const handleVariantDataChange = (e) => {
    const { name, value } = e.target;
    setVariantData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitVariant = async (e) => {
    e.preventDefault();

    if (!variantData.product_code || !variantData.name || !variantData.initial_weight_kg) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    try {
      setIsSubmittingVariant(true);

      await productService.createVariant(selectedProductForVariant.id, {
        product_code: variantData.product_code,
        name: variantData.name,
        initial_weight_kg: parseFloat(variantData.initial_weight_kg),
      });

      toast.success("Variante criada com sucesso!");
      handleCloseVariantModal();

      // Recarregar produtos
      const fetchedProducts = await productService.getProducts(filters);
      setProducts(fetchedProducts);
    } catch (err) {
      toast.error(err.message || "Erro ao criar variante");
      console.error("Erro ao criar variante:", err);
    } finally {
      setIsSubmittingVariant(false);
    }
  };

  // Handlers para reversão de produção ou rejeição
  const handleOpenUndoModal = (productId, productName) => {
    setUndoProductId(productId);
    setUndoProductName(productName);
    setUndoCategory(null);
    setAvailableItems([]);
    setSelectedItem(null);
    setShowUndoModal(true);
  };

  const handleSelectCategory = async (category) => {
    setUndoCategory(category);
    setAvailableItems([]);
    setSelectedItem(null);
    setLoadingItems(true);

    try {
      if (category === 'rejection') {
        // Buscar rejeições do produto
        const response = await productionService.getProductRejections(undoProductId);
        if (response.rejections && response.rejections.length > 0) {
          setAvailableItems(response.rejections);
          setSelectedItem(response.rejections[0].id); // Pre-selecionar a primeira
        } else {
          toast.error("Nenhuma rejeição encontrada para este produto");
          setUndoCategory(null);
        }
      } else if (category === 'production') {
        // Buscar produções do produto
        const response = await productionService.getProductProductions(undoProductId);
        if (response.productions && response.productions.length > 0) {
          setAvailableItems(response.productions);
          setSelectedItem(response.productions[0].id); // Pre-selecionar a primeira
        } else {
          toast.error("Nenhuma produção encontrada para este produto");
          setUndoCategory(null);
        }
      }
    } catch (error) {
      toast.error("Erro ao buscar itens: " + error.message);
      setUndoCategory(null);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleCloseUndoModal = () => {
    setShowUndoModal(false);
    setUndoProductId(null);
    setUndoProductName(null);
    setUndoCategory(null);
    setAvailableItems([]);
    setSelectedItem(null);
  };

  if (loading) {
    return (
      <div className="container-fluid p-4 sm:p-6 lg:p-8 text-center">
        A carregar stock...
      </div>
    );
  }

  return (
    <div className="container-fluid p-4 sm:p-6 lg:p-8">
      {/* Header responsivo */}
      <div className="mb-6">
        <h1 className="text-2xl mb-4">Stock</h1>
        
        {/* Filtros reorganizados para mobile */}
        <div className="space-y-3 lg:space-y-0 lg:flex lg:justify-between lg:items-center">
          {/* Busca e categoria */}
          <div className="flex flex-col sm:flex-row gap-3 lg:flex-row">
            <div className="relative flex-1 sm:flex-initial">
              <input
                type="text"
                placeholder="Buscar produto..."
                name="searchTerm"
                value={localSearchTerm} // Usa o estado local para o input
                onChange={handleFilterChange}
                className="w-full pl-10 pr-4 py-2 border border-neutral-200 rounded-lg"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-3 text-neutral-400"
              />
            </div>
          </div>
          
          {/* Botões */}
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={handleOpenTransferModal}
              className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <FontAwesomeIcon icon={faRightLeft} className="mr-2" />
              Transferir Lote
            </button>
            <button
              onClick={handleOpenWithdrawalModal}
              className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <FontAwesomeIcon icon={faMinus} className="mr-2" />
              Quebra Manual
            </button>
            <button
              onClick={handleOpenInactiveProductsModal}
              className="w-full sm:w-auto px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50"
            >
              <FontAwesomeIcon icon={faRotateLeft} className="mr-2" />
              Desativados
            </button>
            <button
              onClick={handleCreateProduct}
              className="w-full sm:w-auto px-4 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Novo Produto
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {products.length > 0 ? (
          paginatedProducts.map((product) => (
            <div key={product.id} className="bg-white rounded-lg shadow p-4 sm:p-6 w-full">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg">{product.name}</h3>
                    {product.is_variant && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                        Variante
                      </span>
                    )}
                  </div>
                  <p className="text-neutral-600">
                    Código: {product.product_code}
                  </p>
                </div>
                <div className="flex space-x-2">
                  {process.env.NODE_ENV === 'development' && (
                    <button
                      onClick={() => handleOpenUndoModal(product.id, product.name)}
                      className="text-amber-600 hover:text-amber-900"
                      title="Reverter produção ou rejeição"
                    >
                      ↩
                    </button>
                  )}
                  {!product.is_variant && (
                    <button
                      onClick={() => handleOpenVariantModal(product)}
                      className="text-purple-600 hover:text-purple-900"
                      title="Criar variante"
                    >
                      <FontAwesomeIcon icon={faCopy} className="text-base" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDeactivateProduct(product)}
                    disabled={deactivatingProductId === product.id}
                    className="text-red-600 hover:text-red-900 disabled:opacity-40"
                    title="Desativar produto"
                  >
                    <FontAwesomeIcon icon={faBan} className="text-xl" />
                  </button>
                  <button
                    onClick={() => handleEditProduct(product.id)}
                    className="text-neutral-600 hover:text-neutral-900"
                  >
                    <FontAwesomeIcon icon={faPenToSquare} className="text-xl" />
                  </button>
                  <button
                    onClick={() => handleViewDetails(product.id)}
                    className="text-neutral-600 hover:text-neutral-900"
                  >
                    <FontAwesomeIcon
                      icon={faMagnifyingGlass}
                      className="text-xl"
                    />
                  </button>
                </div>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  {product.is_cured && product.curing_stock_kg > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-neutral-600">Em Cura:</span>
                      <span className="text-lg text-orange-600">
                        {(product.curing_stock_kg || 0).toFixed(2)} kg
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-neutral-600">Disponível:</span>
                    <span className="text-lg text-green-600">
                      {(product.current_stock_kg || 0).toFixed(2)} kg
                    </span>
                  </div>
                </div>
                <div className="h-4 bg-neutral-100 rounded-full overflow-hidden flex mt-4">
                  {/* Barra de progresso para stock em cura - primeiro */}
                  {product.is_cured && product.curing_stock_kg > 0 && (
                    <div
                      className="h-full bg-orange-500"
                      style={{
                        width: `${
                          ((product.curing_stock_kg || 0) /
                            (product.max_stock_quantity || 1)) *
                          100
                        }%`,
                      }}
                      title={`Em Cura: ${(product.curing_stock_kg || 0).toFixed(
                        2
                      )} kg`}
                    ></div>
                  )}
                  {/* Barra de progresso para stock disponível - segundo */}
                  <div
                    className="h-full bg-green-500"
                    style={{
                      width: `${
                        ((product.current_stock_kg || 0) /
                          (product.max_stock_quantity || 1)) *
                        100
                      }%`,
                    }}
                    title={`Disponível: ${(
                      product.current_stock_kg || 0
                    ).toFixed(2)} kg`}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-neutral-500">
                  <span>Stock Mín: {product.min_stock_quantity} kg</span>
                  <span>Stock Máx: {product.max_stock_quantity} kg</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <div className="text-neutral-500 mb-6">
              <p className="text-lg mb-2">Nenhum produto disponível no stock</p>
              <p className="text-sm">Crie um novo produto para começar</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={handleCreateProduct}
                className="px-6 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Criar Novo Produto
              </button>
              <button
                onClick={handleOpenWithdrawalModal}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <FontAwesomeIcon icon={faMinus} className="mr-2" />
                Quebra Manual
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-8 gap-4">
          <div className="text-sm text-neutral-600">
            Mostrando {startIndex + 1} a {Math.min(endIndex, totalItems)} de {totalItems} produtos
          </div>
          <nav className="flex items-center space-x-2">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-neutral-200 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            {[...Array(Math.min(5, totalPages))].map((_, index) => {
              let pageNumber;
              if (totalPages <= 5) {
                pageNumber = index + 1;
              } else if (currentPage <= 3) {
                pageNumber = index + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNumber = totalPages - 4 + index;
              } else {
                pageNumber = currentPage - 2 + index;
              }

              return (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={`px-4 py-2 rounded-lg ${
                    currentPage === pageNumber
                      ? 'bg-neutral-900 text-white'
                      : 'border border-neutral-200 hover:bg-neutral-50'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-neutral-200 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </nav>
        </div>
      )}

      {/* Modal de Produtos Desativados */}
      {showInactiveProductsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl mx-4">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="text-xl font-semibold">Produtos desativados</h2>
                <p className="text-sm text-neutral-500 mt-1">
                  Reative produtos para voltarem a aparecer no stock.
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseInactiveProductsModal}
                className="px-3 py-1.5 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200"
              >
                Fechar
              </button>
            </div>

            {loadingInactiveProducts ? (
              <div className="py-10 text-center text-neutral-500">
                A carregar produtos desativados...
              </div>
            ) : inactiveProducts.length === 0 ? (
              <div className="py-10 text-center text-neutral-500 border border-dashed border-neutral-300 rounded-lg">
                Nenhum produto desativado.
              </div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto border border-neutral-200 rounded-lg divide-y divide-neutral-100">
                {inactiveProducts.map((product) => (
                  <div
                    key={product.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4"
                  >
                    <div>
                      <div className="font-medium text-neutral-900">{product.name}</div>
                      <div className="text-sm text-neutral-500">
                        Código: {product.product_code || "Sem código"} · Disponível:{" "}
                        {(product.current_stock_kg || 0).toFixed(2)} kg
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleReactivateProduct(product.id)}
                      disabled={reactivatingProductId === product.id}
                      className="w-full sm:w-auto px-4 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 disabled:opacity-50"
                    >
                      {reactivatingProductId === product.id ? "Reativando..." : "Reativar"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Quebra Manual */}
      {showWithdrawalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-semibold mb-4">Baixa Manual de Estoque</h2>
            <form onSubmit={handleSubmitWithdrawal}>
              <div className="space-y-4">

                {/* Seletor de Tipo */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Tipo de Baixa *
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => { setWithdrawalType('quebra'); setSelectedProduct(""); }}
                      className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                        withdrawalType === 'quebra'
                          ? 'bg-red-600 text-white border-red-600'
                          : 'bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50'
                      }`}
                    >
                      Quebra de Estoque
                    </button>
                    <button
                      type="button"
                      onClick={() => { setWithdrawalType('cura'); setSelectedProduct(""); }}
                      className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                        withdrawalType === 'cura'
                          ? 'bg-orange-500 text-white border-orange-500'
                          : 'bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50'
                      }`}
                    >
                      Correção de Cura
                    </button>
                  </div>
                  <p className="text-xs text-neutral-500 mt-1">
                    {withdrawalType === 'quebra'
                      ? 'Remove estoque disponível (lotes prontos)'
                      : 'Remove estoque que está em processo de cura'}
                  </p>
                </div>

                {/* Select de Produto */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Produto *
                  </label>
                  <select
                    value={selectedProduct}
                    onChange={(e) => setSelectedProduct(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900"
                    required
                  >
                    <option value="">Selecione um produto</option>
                    {getModalProducts().map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name} ({product.product_code}) -{' '}
                        {withdrawalType === 'cura'
                          ? `Em cura: ${product.curing_quantity_kg || product.curing_stock_kg || 0} kg`
                          : `Disponível: ${product.current_stock_kg} kg`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Select de Lote */}
                {selectedProduct && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Lote *
                    </label>
                    {loadingLots ? (
                      <div className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-neutral-500">
                        Carregando lotes...
                      </div>
                    ) : eligibleLots.length === 0 ? (
                      <div className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-red-600 bg-red-50">
                        {withdrawalType === 'cura'
                          ? 'Nenhum lote em cura para este produto'
                          : 'Nenhum lote disponível para este produto'}
                      </div>
                    ) : (
                      <select
                        value={selectedLot}
                        onChange={(e) => setSelectedLot(e.target.value)}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900"
                        required
                      >
                        <option value="">Selecione um lote</option>
                        {eligibleLots
                          .map((lot) => {
                            const lotDate = parseLotDate(lot.lot_number) || new Date(lot.production_date).toLocaleDateString('pt-BR');
                            return (
                              <option key={lot.id} value={lot.id}>
                                {lot.lot_number} - Qtd: {lot.available_quantity} kg - {lotDate}
                              </option>
                            );
                          })}
                      </select>
                    )}
                  </div>
                )}

                {/* Input de Quantidade */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Quantidade (kg) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={selectedLot ? eligibleLots.find(l => l.id === selectedLot)?.available_quantity : undefined}
                    value={withdrawalQuantity}
                    onChange={(e) => setWithdrawalQuantity(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900"
                    placeholder="Ex: 10.50"
                    required
                    disabled={!selectedLot}
                  />
                  {selectedLot && (
                    <p className="text-sm text-neutral-500 mt-1">
                      Máximo no lote: {eligibleLots.find(l => l.id === selectedLot)?.available_quantity} kg
                    </p>
                  )}
                </div>

                {/* Select de Motivo */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    {withdrawalType === 'cura' ? 'Motivo da Correção *' : 'Motivo da Quebra *'}
                  </label>
                  <select
                    value={withdrawalReason}
                    onChange={(e) => setWithdrawalReason(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900"
                    required
                  >
                    <option value="">Selecione um motivo</option>
                    {breakageReasons.map((reason) => (
                      <option key={reason} value={reason}>
                        {reason}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Textarea de Observações */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Observações
                  </label>
                  <textarea
                    value={withdrawalObservations}
                    onChange={(e) => setWithdrawalObservations(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900"
                    placeholder="Informações adicionais (opcional)"
                    rows="3"
                  />
                </div>
              </div>

              {/* Botões de Ação */}
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={handleCloseWithdrawalModal}
                  className="px-4 py-2 bg-neutral-200 text-neutral-800 rounded-lg hover:bg-neutral-300"
                  disabled={isSubmittingWithdrawal}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                    withdrawalType === 'cura'
                      ? 'bg-orange-500 hover:bg-orange-600'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                  disabled={isSubmittingWithdrawal}
                >
                  {isSubmittingWithdrawal
                    ? "Processando..."
                    : withdrawalType === 'cura' ? "Confirmar Correção" : "Confirmar Quebra"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Transferência de Lote entre Produtos */}
      {showTransferModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-semibold mb-4">Transferir Lote entre Produtos</h2>
            <form onSubmit={handleSubmitTransfer}>
              <div className="space-y-4">
                {/* Select de Produto de Origem */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Produto de Origem *
                  </label>
                  <select
                    value={transferSourceProductId}
                    onChange={(e) => setTransferSourceProductId(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900"
                    required
                  >
                    <option value="">Selecione um produto</option>
                    {products
                      .filter((p) => !p.is_variant && p.current_stock_kg > 0)
                      .map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.name} ({product.product_code}) - Disponível: {product.current_stock_kg} kg
                        </option>
                      ))}
                  </select>
                </div>

                {/* Select de Produto de Destino */}
                {transferSourceProductId && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Produto de Destino *
                    </label>
                    {loadingTransferData ? (
                      <div className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-neutral-500">
                        Carregando produtos compatíveis...
                      </div>
                    ) : transferCompatibleProducts.length === 0 ? (
                      <div className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-red-600 bg-red-50 text-sm">
                        Nenhum produto compatível configurado. Configure em Editar Produto.
                      </div>
                    ) : (
                      <select
                        value={transferTargetProductId}
                        onChange={(e) => setTransferTargetProductId(e.target.value)}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900"
                        required
                      >
                        <option value="">Selecione um produto</option>
                        {transferCompatibleProducts.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {/* Select de Lote */}
                {transferSourceProductId && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Lote de Origem *
                    </label>
                    {loadingTransferData ? (
                      <div className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-neutral-500">
                        Carregando lotes...
                      </div>
                    ) : transferSourceLots.length === 0 ? (
                      <div className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-red-600 bg-red-50">
                        Nenhum lote disponível para este produto
                      </div>
                    ) : (
                      <select
                        value={transferLotId}
                        onChange={(e) => setTransferLotId(e.target.value)}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900"
                        required
                      >
                        <option value="">Selecione um lote</option>
                        {transferSourceLots.map((lot) => (
                          <option key={lot.id} value={lot.id}>
                            {lot.lot_number} - Qtd: {lot.available_quantity} kg
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {/* Input de Quantidade */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Quantidade (kg) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={transferLotId ? transferSourceLots.find(l => l.id === transferLotId)?.available_quantity : undefined}
                    value={transferQuantity}
                    onChange={(e) => setTransferQuantity(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900"
                    placeholder="Ex: 10.50"
                    required
                    disabled={!transferLotId}
                  />
                  {transferLotId && (
                    <p className="text-sm text-neutral-500 mt-1">
                      Máximo no lote: {transferSourceLots.find(l => l.id === transferLotId)?.available_quantity} kg
                    </p>
                  )}
                </div>

                {/* Nome do novo lote */}
                {transferTargetProductId && transferLotId && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Nome do novo lote
                    </label>
                    <input
                      type="text"
                      value={transferNewLotName}
                      onChange={(e) => setTransferNewLotName(e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900"
                      placeholder="Ex: GRV-12012026"
                    />
                    <p className="text-xs text-neutral-400 mt-1">
                      Preenchido automaticamente. Pode alterar se necessário.
                    </p>
                  </div>
                )}
              </div>

              {/* Botões de Ação */}
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={handleCloseTransferModal}
                  className="px-4 py-2 bg-neutral-200 text-neutral-800 rounded-lg hover:bg-neutral-300"
                  disabled={isSubmittingTransfer}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  disabled={isSubmittingTransfer}
                >
                  {isSubmittingTransfer ? "Transferindo..." : "Confirmar Transferência"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Criação de Variante */}
      {showVariantModal && selectedProductForVariant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-semibold mb-4">Criar Variante de Produto</h2>
            <p className="text-sm text-neutral-600 mb-4">
              Criando variante de: <strong>{selectedProductForVariant.name}</strong>
            </p>
            <form onSubmit={handleSubmitVariant}>
              <div className="space-y-4">
                {/* Código do Produto */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Código do Produto *
                  </label>
                  <input
                    type="text"
                    name="product_code"
                    value={variantData.product_code}
                    onChange={handleVariantDataChange}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900"
                    placeholder="Ex: QJ001-V1"
                    required
                  />
                </div>

                {/* Nome do Produto */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Nome do Produto *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={variantData.name}
                    onChange={handleVariantDataChange}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900"
                    placeholder="Ex: Queijo Mussarela 500g"
                    required
                  />
                </div>

                {/* Peso por Unidade */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Peso por Unidade (kg) *
                  </label>
                  <input
                    type="number"
                    name="initial_weight_kg"
                    step="0.01"
                    min="0.01"
                    value={variantData.initial_weight_kg}
                    onChange={handleVariantDataChange}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900"
                    placeholder="Ex: 0.5"
                    required
                  />
                  <p className="text-xs text-neutral-500 mt-1">
                    Este peso pode ser diferente do produto pai. O stock e lote serão compartilhados.
                  </p>
                </div>

                {/* Informação sobre herança */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Atenção:</strong> A variante herdará todas as outras propriedades do produto pai
                    (descrição, tempo de cura, tipo de caixa, etc.). O stock e os lotes serão compartilhados
                    com o produto pai.
                  </p>
                </div>
              </div>

              {/* Botões de Ação */}
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={handleCloseVariantModal}
                  className="px-4 py-2 bg-neutral-200 text-neutral-800 rounded-lg hover:bg-neutral-300"
                  disabled={isSubmittingVariant}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  disabled={isSubmittingVariant}
                >
                  {isSubmittingVariant ? "Criando..." : "Criar Variante"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Reversão (Produção ou Rejeição) */}
      {showUndoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-semibold mb-4">
              Reverter - {undoProductName}
            </h2>

            {!undoCategory ? (
              // Tela de seleção de categoria
              <div className="space-y-4">
                <p className="text-neutral-600 text-sm mb-6">
                  Selecione o tipo de reversão que deseja fazer:
                </p>

                <div className="grid grid-cols-1 gap-3">
                  <button
                    onClick={() => handleSelectCategory('production')}
                    disabled={loadingItems}
                    className="w-full px-4 py-3 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 transition text-sm font-medium"
                  >
                    📦 Reverter Produção
                  </button>

                  <button
                    onClick={() => handleSelectCategory('rejection')}
                    disabled={loadingItems}
                    className="w-full px-4 py-3 border-2 border-red-500 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 transition text-sm font-medium"
                  >
                    ❌ Reverter Rejeição
                  </button>
                </div>

                <div className="pt-4 border-t border-neutral-200">
                  <button
                    onClick={handleCloseUndoModal}
                    className="w-full px-4 py-2 bg-neutral-200 text-neutral-800 rounded-lg hover:bg-neutral-300"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              // Tela de seleção de item
              <div className="space-y-4">
                {loadingItems ? (
                  <div className="text-center text-neutral-600 text-sm py-6">
                    Carregando {undoCategory === 'rejection' ? 'rejeições' : 'produções'}...
                  </div>
                ) : availableItems.length > 0 ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Selecione qual {undoCategory === 'rejection' ? 'rejeição' : 'produção'} deseja reverter:
                      </label>
                      <select
                        value={selectedItem || ""}
                        onChange={(e) => setSelectedItem(e.target.value)}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="">Selecione um item</option>
                        {availableItems.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.display}
                          </option>
                        ))}
                      </select>
                    </div>

                    <p className="text-neutral-600 text-xs bg-yellow-50 p-3 rounded border border-yellow-200">
                      ⚠️ <strong>Atenção:</strong> Ao reverter, a quantidade será restaurada ao estoque e o lote será recuperado.
                    </p>

                    <div className="flex gap-2 pt-4 border-t border-neutral-200">
                      <button
                        onClick={() => {
                          setUndoCategory(null);
                          setAvailableItems([]);
                          setSelectedItem(null);
                        }}
                        className="flex-1 px-4 py-2 bg-neutral-200 text-neutral-800 rounded-lg hover:bg-neutral-300 text-sm"
                      >
                        Voltar
                      </button>
                      <button
                        onClick={async () => {
                          if (selectedItem) {
                            setIsSubmittingUndo(true);
                            try {
                              const selectedItemData = availableItems.find(item => item.id === selectedItem);
                              if (!selectedItemData) {
                                toast.error("Item não encontrado");
                                return;
                              }

                              // Chamar reversão com order_id
                              await productionService.undoLastChange(
                                selectedItemData.order_id,
                                undoProductId
                              );

                              toast.success(
                                `${undoCategory === 'rejection' ? 'Rejeição' : 'Produção'} revertida com sucesso!`
                              );

                              // Recarregar products
                              const fetchedProducts = await productService.getProducts(filters);
                              setProducts(fetchedProducts);

                              handleCloseUndoModal();
                            } catch (error) {
                              toast.error("Erro ao reverter: " + error.message);
                            } finally {
                              setIsSubmittingUndo(false);
                            }
                          }
                        }}
                        disabled={!selectedItem || isSubmittingUndo}
                        className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                      >
                        {isSubmittingUndo ? "Revertendo..." : "Reverter"}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-center text-neutral-600 text-sm py-6">
                      Nenhum item encontrado.
                    </p>
                    <button
                      onClick={() => {
                        setUndoCategory(null);
                        setAvailableItems([]);
                      }}
                      className="w-full px-4 py-2 bg-neutral-200 text-neutral-800 rounded-lg hover:bg-neutral-300"
                    >
                      Voltar
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Stock;
